import { extend, override } from 'flarum/common/extend';
import Post from 'flarum/common/models/Post';
// Emoji in French (Unicode CLDR's names and keywords - see
// scripts/emojiNamesFr.mjs). Names, e.g. "Visage faisant un clin d’œil",
// shown on hover:
// - in the composer's suggestions (flarum/emoji's ":" dropdown, also what
//   its smiley toolbar button opens), which showed the first English name
//   from its emojiMap ("wink");
// - on the emoji of a published post (flarum/emoji's twemoji images),
//   which showed nothing - phpBB showed "Clin d'oeil".
// And the composer's ":" search also finds an emoji by the words of its
// French name and keywords (":clin", ":champignon", ":coeur"), not only by
// its English ones - without accents, which flarum/emoji's autocomplete
// doesn't accept after the ":" (it stops at the first "é").
//
// Loaded on demand, in a separate chunk like flarum/emoji's own emojiMap:
// when an editor opens, or the first time a post with an emoji is shown.
// An emoji with no French name keeps the English one (the 26 regional
// indicator letters).

let names = null;
let loading = null;

// Never rejects: the editor waits for every loader before it builds, so a
// chunk failing to load must cost the French names, not the editor.
function loadNames() {
  loading ??= import(/* webpackChunkName: 'forum/emojiNamesFr', webpackMode: 'lazy-once' */ './emojiNamesFr').then(module => {
    names = module.default;
  }).catch(() => {});
  return loading;
}
let search = null;
let searchLoading = null;
function loadSearch() {
  searchLoading ??= import(/* webpackChunkName: 'forum/emojiSearchFr', webpackMode: 'lazy-once' */ './emojiSearchFr').then(module => {
    search = module.default;
  }).catch(() => {});
  return searchLoading;
}
let searchAdded = false;
// Appends each emoji's French words to its list of names in flarum/emoji's
// own emojiMap - the very object its search loops over - so nothing else of
// it needs changing. Its first entry stays the English name it used as the
// tooltip, which the dropdown override below replaces anyway.
//
// That object isn't in the export registry (flarum.reg.get() doesn't know
// it, so neither does asyncModuleImport()): it is required here from
// flarum/emoji's own webpack runtime, which only exists once flarum/emoji
// has required a module of its own - hence once the editor is built, after
// its emojiMap loader. (Calling asyncModuleImport() earlier fell back to
// core's runtime, which never sees other extensions' chunks: a
// ChunkLoadError that kept the editor from building at all.)
function addFrenchSearch() {
  if (searchAdded || !search) return;
  const runtime = flarum.reg._webpack_runtimes['flarum-emoji'];
  const moduleId = flarum.reg.chunkModules.get('flarum-emoji:forum/emojiMap')?.moduleId;
  let emojiMap;
  try {
    emojiMap = runtime && moduleId ? runtime(moduleId).default : undefined;
  } catch {
    // Not loaded after all: English search only.
  }
  if (!emojiMap) return;
  searchAdded = true;
  for (const [emoji, words] of Object.entries(search)) {
    emojiMap[emoji]?.push(...words.split(' '));
  }
}
function frenchName(emoji) {
  const name = names?.[emoji] ?? names?.[emoji.replace(/\uFE0F/g, '')];
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : null;
}
function findEmojiImage(node) {
  if (!node || typeof node !== 'object') return null;
  const vnode = node;
  if (vnode.tag === 'img' && vnode.attrs?.className === 'emoji') return vnode;
  for (const child of Array.isArray(vnode.children) ? vnode.children : []) {
    const image = findEmojiImage(child);
    if (image) return image;
  }
  return null;
}
function escapeAttribute(text) {
  return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// The HTML of each post as last returned, with titles added - recomputed
// only when flarum/emoji's own output changes.
const titledHtml = new WeakMap();
function addTitles(html) {
  return html.replace(/<img\b[^>]*\bclass="emoji"[^>]*>/g, tag => {
    if (/\stitle=/.test(tag)) return tag;
    const alt = tag.match(/\salt="([^"]*)"/)?.[1];
    const name = alt ? frenchName(alt) : null;
    return name ? tag.replace(/^<img\b/, `<img title="${escapeAttribute(name)}"`) : tag;
  });
}
export default function showFrenchEmojiNames() {
  // Same mechanism flarum/emoji uses to load its own emojiMap before the
  // editor (and so its suggestions) is built.
  extend('flarum/common/components/TextEditor', 'oninit', function () {
    this._loaders.push(loadNames, loadSearch);
  });

  // Every loader has resolved by then, flarum/emoji's included.
  extend('flarum/common/components/TextEditor', 'onbuild', addFrenchSearch);

  // Every suggestion is a <Tooltip text={englishName}> around a button with
  // an <img alt={emoji} title={englishName}>, built inside flarum/emoji's
  // own input listener - translated here, right before the list renders.
  override('ext:flarum/emoji/forum/fragments/AutocompleteDropdown', 'view', function (original) {
    for (const suggestion of this.items) {
      const image = findEmojiImage(suggestion);
      const name = image ? frenchName(String(image.attrs.alt)) : null;
      if (image && name) {
        suggestion.attrs.text = name;
        image.attrs.title = name;
      }
    }
    return original();
  });

  // Wraps flarum/emoji's own contentHtml() override (its initializer runs
  // before this extension's: composer.json's optional-dependencies), so the
  // twemoji <img>s are already there.
  override(Post.prototype, 'contentHtml', function (original) {
    const html = original();
    if (!html || !html.includes('class="emoji"')) return html;
    if (!names) {
      // Posts already on screen aren't re-rendered when the names arrive:
      // core's CommentPost caches its content (subtree) and only rebuilds
      // it when the post itself changes - so their emoji get the title here.
      loadNames().then(() => {
        document.querySelectorAll('.Post-body img.emoji:not([title])').forEach(image => {
          const name = frenchName(image.alt);
          if (name) image.title = name;
        });
      });
      return html;
    }
    const cached = titledHtml.get(this);
    if (cached?.[0] === html) return cached[1];
    const titled = addTitles(html);
    titledHtml.set(this, [html, titled]);
    return titled;
  });
}flarum.reg.addChunkModule('115', '705', 'champis-forum', 'forum/emojiNamesFr');
flarum.reg.addChunkModule('529', '797', 'champis-forum', 'forum/emojiSearchFr');